import { isRecipeStockProduct } from './stockTakeRecipeProduct'
import { findVenueProductForStockTakeProduct } from './stockTakeConversions'
import {
  getParLevelForProduct,
  parseParLevelValue,
  previewParLevelSave,
} from './parLevelPaywall'
import { buildVenueProductSavePayload } from './venueProductSavePayload'

export const venueProductForStockTakeLine = (line, venueProducts = []) => {
  if (!line || isRecipeStockProduct(line) || line.isLinkedStockGroup) {
    return null
  }
  const matched = findVenueProductForStockTakeProduct(line, venueProducts)
  // Inactive shopping-list rows cannot be ordered — treat as not on the list.
  if (matched?.isActive === false) return null
  return matched
}

export const canEditStockTakeLinePar = (line, venueProducts = []) =>
  Boolean(venueProductForStockTakeLine(line, venueProducts)?._id)

export const STOCK_TAKE_PAR_NOT_ON_LIST_COPY =
  "This product isn't active in the shopping list. Add it back into your shopping list to order it again."

export const stockTakeParUnavailableReason = (line, venueProducts = []) => {
  if (!line) return null
  if (isRecipeStockProduct(line) || line.isLinkedStockGroup)
    return 'not_orderable'
  if (canEditStockTakeLinePar(line, venueProducts)) return null
  return 'not_on_shopping_list'
}

export const stockTakeParUnavailableCopy = (line, venueProducts = []) => {
  if (
    stockTakeParUnavailableReason(line, venueProducts) !==
    'not_on_shopping_list'
  ) {
    return ''
  }
  return STOCK_TAKE_PAR_NOT_ON_LIST_COPY
}

export const isParLevelSet = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0
}

export const displayParLevelForStockLine = (
  line,
  venueProducts = [],
  parEdits = {},
) => {
  const venueProduct = venueProductForStockTakeLine(line, venueProducts)
  if (!venueProduct) return null
  const edited = parEdits[String(venueProduct._id)]
  const raw =
    edited !== undefined && edited !== null
      ? parseParLevelValue(edited)
      : getParLevelForProduct(venueProduct)
  return isParLevelSet(raw) ? raw : null
}

export const applyStockTakeParEdits = (venueProducts = [], parEdits = {}) =>
  (Array.isArray(venueProducts) ? venueProducts : []).map(product => {
    const edited = parEdits[String(product?._id)]
    if (edited === undefined || edited === null) return product
    return { ...product, parLevel: parseParLevelValue(edited) }
  })

/**
 * Pending par saves through the existing venue-product save, with the Free
 * cap of 10 applied in order (same as shopping-list inline save).
 */
export const collectStockTakeParSaves = ({
  parEdits = {},
  venueProducts = [],
  plan = 'Free',
  venueId,
} = {}) => {
  const saves = []
  const simulated = (Array.isArray(venueProducts) ? venueProducts : []).map(
    product => ({ ...product }),
  )

  for (const [venueProductId, rawPar] of Object.entries(parEdits)) {
    const product = simulated.find(
      item => String(item?._id) === String(venueProductId),
    )
    if (!product) continue

    const newParLevel = parseParLevelValue(rawPar)
    const currentParLevel = getParLevelForProduct(product)
    if (newParLevel === currentParLevel) continue

    const preview = previewParLevelSave({
      venueProducts: simulated,
      venueProductId: product._id,
      newParLevel,
      plan,
    })
    if (preview.blocked) {
      return {
        blocked: true,
        product,
        preview,
        saves,
      }
    }

    saves.push({
      product,
      previousParLevel: currentParLevel,
      newParLevel,
      payload: buildVenueProductSavePayload(
        { ...product, parLevel: newParLevel },
        venueId,
      ),
    })
    product.parLevel = newParLevel
  }

  return { blocked: false, saves, preview: null, product: null }
}

export const STOCK_TAKE_COUNTS_SAVED_PARS_FAILED =
  'Counts saved. Par levels did not save. Please try again.'

/** Keep unsaved par drafts; drop keys that already posted. */
export const dropPersistedParEditKeys = (parEdits = {}, persistedIds = []) => {
  const drop = new Set(
    (Array.isArray(persistedIds) ? persistedIds : []).map(String),
  )
  const next = {}
  for (const [key, value] of Object.entries(parEdits || {})) {
    if (!drop.has(String(key))) next[key] = value
  }
  return next
}

export const hasPendingStockTakeParEdits = ({
  parEdits,
  venueProducts,
  plan,
  venueId,
} = {}) =>
  collectStockTakeParSaves({ parEdits, venueProducts, plan, venueId }).saves
    .length > 0
